import { useCallback, useEffect } from 'react';
import { PopupOpenEventArgs } from '@syncfusion/ej2-react-schedule';
import { useSelector, useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { setCulture } from '@syncfusion/ej2-base';
import isEqual from 'lodash/isEqual';
import { selectFacilitatorsResources } from 'state/Schedule/ScheduleSlice';
import {
  getEventTypeIdFromCode,
  attachListenersToSchedule,
} from '../helpers/helpers';
import { FetchedEvent } from 'utils/types/learningSchedule';
import {
  DetailsSectionRenderer,
  FacilitatorsSectionRenderer,
} from '../components/Commons';
import {
  getEventPermissions,
  eventPermissionsSelectors,
} from 'state/normalized/eventPermissions/eventPermissionsSlice';
import { generateExistingEventData } from '../helpers/scheduleCalendarHelper';
import {
  generateDetailsSectionElements,
  processTimesWhenEventTimezonesEnabled,
} from './helpers';

interface UseSchedulePopupProps {
  scheduleObj: React.RefObject<any>;
  eventTimezones: boolean;
  fmEventDescription: boolean;
  locale: string;
  events: any[];
  facilitatorsEvents: any[];
  setProjectIds: (value: string[]) => void;
  setEventTitle: (value: string) => void;
  setEventDescription: (value: string) => void;
  setEventType: (value: string) => void;
  setSelectedFacilitatorsIds: (value: string[]) => void;
  setIsEditingSeriesWithExceptions: (value: boolean) => void;
  setInitialEvent: (value: FetchedEvent | undefined) => void;
  setUpdatedEvent: (value: FetchedEvent | undefined) => void;
  onUpdateEventField: (field: string, value: any) => void;
  resetStateHandler: () => void;
  saveButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  projectIds: string[];
  eventTitle: string;
  eventType: string;
  initialEvent: FetchedEvent | null;
  updatedEvent: FetchedEvent | null;
}

export const useSchedulePopup = ({
  scheduleObj,
  eventTimezones,
  fmEventDescription,
  locale,
  events,
  facilitatorsEvents,
  setProjectIds,
  setEventTitle,
  setEventDescription,
  setEventType,
  setSelectedFacilitatorsIds,
  setIsEditingSeriesWithExceptions,
  setInitialEvent,
  setUpdatedEvent,
  onUpdateEventField,
  resetStateHandler,
  saveButtonRef,
  projectIds,
  eventTitle,
  eventType,
  initialEvent,
  updatedEvent,
}: UseSchedulePopupProps) => {
  const dispatch = useDispatch();
  const eventPermissionsSelector = useSelector(
    eventPermissionsSelectors.selectEventPermissionsById
  );
  const facilitatorsResources = useSelector(selectFacilitatorsResources);

  // Fetch event permissions for popup functionality
  useEffect(() => {
    const eventIds = events.map((event) => event.Id);
    const facilitatorsEventIds = facilitatorsEvents.map((event) => event.Id);
    const setOfIds = new Set([...eventIds, ...facilitatorsEventIds]);
    const scheduleIds = Array.from(setOfIds);

    if (scheduleIds.length) {
      dispatch(getEventPermissions({ scheduleIds }));
    }
  }, [dispatch, events, facilitatorsEvents]);

  const onPopupOpen = useCallback(
    (args: PopupOpenEventArgs) => {
      const eventPermissions = eventPermissionsSelector(args.data?.Id);
      const canUserEdit = eventPermissions?.canEdit;
      const canUserDelete = eventPermissions?.canDelete;
      const quickPopup = document.querySelector('.e-quick-popup-wrapper');
      if (quickPopup) {
        const editButton = quickPopup.querySelector(
          '.e-event-edit'
        ) as HTMLElement;
        const deleteButton = quickPopup.querySelector(
          '.e-event-delete'
        ) as HTMLElement;
        if (editButton && !canUserEdit) editButton.style.display = 'none';
        if (editButton && canUserEdit)
          editButton.textContent = intl.get('SCHEDULE.QUICK_VIEW.VIEW_DETAILS');
        if (deleteButton && !canUserDelete) deleteButton.style.display = 'none';
      }

      let existingEventData: FetchedEvent | undefined;
      let initialProjects: string[] =
        args.data?.ProjectId && args.target ? [args.data?.ProjectId] : [];
      let projectsData: any[] = [];
      let initialFacilitators: { id: string; name: string }[] = [];

      if (args.type === 'RecurrenceValidationAlert') {
        setIsEditingSeriesWithExceptions(true);
      }

      if (args.type === 'Editor') {
        if (args.data?.Id) {
          if (!canUserEdit) {
            args.cancel = true;
            return;
          }
          if (!canUserDelete) {
            const deleteButton = args.element.querySelector(
              '.e-event-delete'
            ) as HTMLElement;
            if (deleteButton) {
              deleteButton.style.display = 'none';
            }
          }
        }

        resetStateHandler();

        if (args.data?.FacilitatorId && args.target) {
          const facilitatorData = facilitatorsResources.find(
            (f: any) => f.FacilitatorId === args.data?.FacilitatorId
          );
          if (facilitatorData) {
            initialFacilitators = [
              { id: facilitatorData.FacilitatorId, name: facilitatorData.Name },
            ];
          }
        }

        if (args.data?.Id) {
          if (args.data?.Subject) {
            const modalTitle = args.element.querySelector('.e-title-text');
            if (modalTitle) {
              modalTitle.textContent = intl.get('SCHEDULE.EDIT_EVENT_TITLE', {
                eventTitle: args.data.Subject,
              });
            }
          }

          const isRecurringEvent = args?.data?.RecurrenceRule !== null;
          let startTime = args.data?.StartTime;
          let endTime = args.data?.EndTime;

          // For multi-day all-day events, EndTime was adjusted +1 for Syncfusion display
          // Subtract 1 day to get the actual end date for editing
          if (args.data?.IsAllDay && startTime && endTime) {
            const startDate = new Date(startTime).toDateString();
            const endDate = new Date(endTime).toDateString();
            if (startDate !== endDate) {
              const adjustedEndTime = new Date(endTime);
              adjustedEndTime.setDate(adjustedEndTime.getDate() - 1);
              endTime = adjustedEndTime;
            }
          }

          let isOldEvent = args.data?.isOldEvent
            ? false
            : args.data?.isOldEvent || true;

          processTimesWhenEventTimezonesEnabled(
            eventTimezones,
            args,
            startTime,
            endTime,
            isRecurringEvent
          );

          const eventTypeId = getEventTypeIdFromCode(
            args.data?.EventType ?? ''
          );
          existingEventData = generateExistingEventData(
            args,
            eventTypeId,
            startTime,
            endTime,
            isOldEvent
          );

          initialFacilitators = args.data?.facilitators || [];
          initialProjects = existingEventData?.projectIds || [];
          setInitialEvent(existingEventData);
          setUpdatedEvent(existingEventData);
          setEventTitle(existingEventData?.subject || '');
          setEventDescription(existingEventData?.description ?? '');
          setEventType(existingEventData?.learningEventType || '');
          projectsData = existingEventData?.projects || [];
        }

        setProjectIds(initialProjects);
        attachListenersToSchedule(args, onUpdateEventField);

        if (!args.element.querySelector('.details-section')) {
          if (scheduleObj) {
            setCulture(locale);
            const recurrenceEditor =
              scheduleObj?.current?.eventWindow?.recurrenceEditor;
            if (recurrenceEditor) {
              recurrenceEditor.frequencies = [
                'none',
                'daily',
                'weekly',
                'monthly',
              ];
            }
          }

          const { detailsContainer, facilitatorsContainer } =
            generateDetailsSectionElements(args, saveButtonRef);

          DetailsSectionRenderer({
            existingEventData: existingEventData,
            projectsData: projectsData,
            fmEventDescription,
            setEventTitle: setEventTitle,
            setEventDescription: setEventDescription,
            setProjectIds: setProjectIds,
            setEventType: setEventType,
            onUpdateEventField: onUpdateEventField,
            initialProjects: initialProjects,
            detailsContainer: detailsContainer,
          });

          FacilitatorsSectionRenderer({
            initialFacilitators: initialFacilitators,
            facilitatorsContainer: facilitatorsContainer,
            setSelectedFacilitatorsIds: setSelectedFacilitatorsIds,
            onUpdateEventField: onUpdateEventField,
          });
        }
      } else if (args.type === 'RecurrenceAlert') {
        if (!canUserEdit) {
          args.cancel = true;
          return;
        }
        const dialogHeader = args.element.querySelector('#QuickDialog_title');
        const dialogContent = args.element.querySelector(
          '#QuickDialog_dialog-content'
        );
        const seriesButton = args.element.querySelector(
          'button.e-quick-dialog-series-event'
        );

        if (dialogContent && seriesButton) {
          if (dialogHeader?.textContent === 'Delete Event') {
            dialogContent.textContent = intl.get(
              'SCHEDULE.EVENTS.DELETE_RECURRING_EVENT_MESSAGE'
            );
            seriesButton.textContent = intl.get(
              'SCHEDULE.EVENTS.DELETE_SERIES_BUTTON'
            );
          } else if (dialogHeader?.textContent === 'Edit Event') {
            dialogContent.textContent = intl.get(
              'SCHEDULE.EVENTS.EDIT_RECURRING_EVENT_MESSAGE'
            );
            seriesButton.textContent = intl.get(
              'SCHEDULE.EVENTS.EDIT_SERIES_BUTTON'
            );
          }
        }
      }
    },
    [
      eventPermissionsSelector,
      facilitatorsResources,
      fmEventDescription,
      scheduleObj,
      eventTimezones,
      locale,
      setProjectIds,
      setEventTitle,
      setEventDescription,
      setEventType,
      setSelectedFacilitatorsIds,
      setIsEditingSeriesWithExceptions,
      setInitialEvent,
      setUpdatedEvent,
      onUpdateEventField,
      resetStateHandler,
      saveButtonRef,
    ]
  );

  // Handle save button state for editing existing events
  useEffect(() => {
    if (saveButtonRef?.current) {
      if (
        updatedEvent &&
        initialEvent &&
        !isEqual(updatedEvent, initialEvent) &&
        updatedEvent.subject &&
        updatedEvent.learningEventType
      ) {
        saveButtonRef.current.disabled = false;
      } else {
        saveButtonRef.current.disabled = true;
      }
    }
  }, [updatedEvent, initialEvent, saveButtonRef]);

  // Handle save button state for creating new events
  useEffect(() => {
    if (updatedEvent || initialEvent) {
      return;
    }
    if (eventTitle && eventType) {
      if (saveButtonRef && saveButtonRef.current) {
        saveButtonRef.current.disabled = false;
      }
    } else {
      if (saveButtonRef && saveButtonRef.current) {
        saveButtonRef.current.disabled = true;
      }
    }
  }, [eventTitle, eventType, saveButtonRef, updatedEvent, initialEvent]);

  return { onPopupOpen };
};
